/**
 * System Notice create/edit form — date/time range, markdown message,
 * enabled toggle, and staff-only flag for the system banner admin feature.
 */

import { FormControlLabel, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { addDays, isAfter, parseISO, setHours, setMinutes } from 'date-fns';
import { t } from 'i18next';
import React from 'react';

import SystemNoticeMessageBox from '@sophocles/views/admin/components/SystemNoticeMessageBox';

class SystemNoticeForm extends React.Component {
  constructor(props) {
    super(props);
    const now = new Date();
    this.state = {
      enabled: true,
      staff_only: false,
      start_time: now,
      end_time: addDays(now, 1),
      message: '',
      errors: false,
    };
  }

  componentDidMount() {
    const {
      match: { params },
      fetchSystemBanner,
    } = this.props;

    if (params.id !== 'new') {
      fetchSystemBanner(params.id).then(res => {
        if (res && !res.error) {
          this.setState({
            enabled: res.enabled,
            staff_only: res.staff_only ?? false,
            start_time: parseISO(res.start_time),
            end_time: parseISO(res.end_time),
            message: res.message,
          });
        } else {
          this.setState({ errors: { general: 'Failed to load system notice. Please refresh and try again.' } });
        }
      });
    }
  }

  onMessageChange = message => this.setState({ message });

  onToggleEnabled = () => this.setState({ enabled: !this.state.enabled, errors: false });

  onToggleStaffOnly = () => this.setState({ staff_only: !this.state.staff_only, errors: false });

  onSetStartTime = date => this.setState({ start_time: date, errors: false });

  onSetStartDate = date => {
    const old = this.state.start_time;
    return this.setState({
      start_time: setHours(setMinutes(date, old.getMinutes()), old.getHours()),
      errors: false,
    });
  };

  onSetEndTime = date => this.setState({ end_time: date, errors: false });

  onSetEndDate = date => {
    const old = this.state.end_time;
    return this.setState({
      end_time: setHours(setMinutes(date, old.getMinutes()), old.getHours()),
      errors: false,
    });
  };

  handleErrors = error => {
    const errorData = error?.errors ?? {};
    Object.entries(errorData).forEach(([key, value]) => {
      if (key === 'non_field_errors') {
        this.setState({ errors: { non_field_error: value[0] } });
      } else {
        this.setState({ errors: { general: `Unexpected error: ${value}` } });
      }
    });
  };

  redirectToList = () => this.props.router.navigate('/staff/systemnotices/', { replace: true });

  save = () => {
    const {
      match: { params },
      patchSystemBanner,
      createSystemBanner,
    } = this.props;

    const { start_time, end_time, message, enabled, staff_only } = this.state;

    const values = {
      enabled,
      staff_only,
      message,
      start_time,
      end_time,
    };

    if (params.id === 'new') {
      return createSystemBanner(values).then(res =>
        res && !res.error ? this.redirectToList() : this.handleErrors(res?.error ?? {}),
      );
    }

    return patchSystemBanner(params.id, values).then(res =>
      res && !res.error ? this.redirectToList() : this.handleErrors(res?.error ?? {}),
    );
  };

  render() {
    const { enabled, staff_only, start_time, end_time, message, errors } = this.state;

    const dateError = isAfter(start_time, end_time) ? 'Start date must be before end date.' : null;
    const disableSubmit = message === '' || start_time == null || end_time == null || !!dateError || !!errors;

    const errorMessage = dateError || errors?.non_field_error || errors?.general || null;
    const hasError = !!errorMessage;

    return (
      <Stack sx={{ gap: 'var(--space-3)' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('admin.systemNoticeIntro', 'Define the System Notice below.')}
        </Typography>

        <Stack direction="row" sx={{ gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          <FormControlLabel
            label={enabled ? 'Enabled' : 'Disabled'}
            control={<Checkbox onChange={this.onToggleEnabled} checked={enabled} />}
          />
          <FormControlLabel
            label={t('general.staffOnly', 'Staff Only')}
            control={<Checkbox onChange={this.onToggleStaffOnly} checked={staff_only} />}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 'var(--space-2)' }}>
          <DatePicker
            label={t('admin.startDate', 'Start Date')}
            name="start_date"
            value={start_time}
            onChange={this.onSetStartDate}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <TimePicker
            name="start_time"
            format="hh:mm a"
            label={t('general.startTime', 'Start Time')}
            value={start_time}
            onChange={this.onSetStartTime}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 'var(--space-2)' }}>
          <DatePicker
            label={t('admin.endDate', 'End Date')}
            name="end_date"
            value={end_time}
            onChange={this.onSetEndDate}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <TimePicker
            name="end_time"
            format="hh:mm a"
            label={t('general.endTime', 'End Time')}
            value={end_time}
            onChange={this.onSetEndTime}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Stack>

        <Box>
          <Typography variant="body2" sx={{ color: hasError ? 'error.main' : 'text.secondary' }}>
            {hasError ? errorMessage : 'Note: there cannot be two system notices active at the same time.'}
          </Typography>
        </Box>

        <SystemNoticeMessageBox value={message} onChange={this.onMessageChange} />

        <Stack direction="row" justifyContent="flex-end" sx={{ gap: 'var(--space-2)', mt: 'var(--space-3)' }}>
          <Button variant="outlined" color="inherit" onClick={this.redirectToList}>
            {t('actions.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="primary" onClick={this.save} disabled={disableSubmit}>
            {t('actions.submit', 'Submit')}
          </Button>
        </Stack>
      </Stack>
    );
  }
}

export default SystemNoticeForm;
